import { createColumnHelper } from '@tanstack/react-table';
import { EnterpriseLink, LocalizedDateTimeFromUtc, TruncatedText } from '@mews-commander/ui';
import { LocalizedCurrencyValue, lt } from '@mews/framework';
import {
    Button,
    IconName,
    Stack,
    Table,
    TableHeader,
    TableProvider,
    Typography,
    useTableState,
} from '@optimus-web/core';
import { CashierShiftTransaction } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { CashierShiftProps } from '../../../types';
import { useShiftTransactionData } from '../../../hooks/useShiftTransactionData';
import { useCreateCashierTransactionDialog } from '../../modals/transactionCreate/useCreateCashierTransactionDialog';
import { TransactionNumber } from './TransactionNumber';

const helper = createColumnHelper<CashierShiftTransaction>();

const columns = [
    helper.accessor('number', {
        header: () => lt('Number'),
        cell: ({ row }) => <TransactionNumber transaction={row.original} />,
    }),
    helper.accessor('customerName', {
        header: () => lt('Customer'),
        cell: ({ getValue }) => getValue() || UnicodeEntity.Dash,
    }),
    helper.accessor('createdUtc', {
        header: () => lt('Created'),
        cell: ({ getValue, row }) => (
            <Stack vertical>
                <LocalizedDateTimeFromUtc utcString={getValue()} />
                <Typography textStyle="bodyMedium" as="div" variant="tertiary">
                    {row.original.creatorName}
                </Typography>
            </Stack>
        ),
    }),
    helper.accessor('billNumber', {
        header: () => lt('Bill'),
        cell: ({ row }) => {
            const { billId, billNumber } = row.original;
            return !!billNumber && !!billId ? (
                <EnterpriseLink variant="basic" to="Bill/Detail/$id" params={{ id: billId }}>
                    {billNumber}
                </EnterpriseLink>
            ) : (
                UnicodeEntity.Dash
            );
        },
    }),
    helper.accessor('notes', {
        header: () => lt('Notes'),
        cell: ({ getValue }) => {
            const note = getValue();
            return note ? <TruncatedText text={note} maxLength={30} /> : UnicodeEntity.Dash;
        },
    }),
    helper.accessor('amount', {
        header: () => lt('Amount'),
        cell: ({ getValue }) => <LocalizedCurrencyValue value={getValue()} />,
    }),
];

export const ShiftTransactionsTable = ({ cashierId, shiftId }: CashierShiftProps) => {
    const [tableState, setTableState] = useTableState<CashierShiftTransaction>({
        pagination: {
            show: true,
        },
    });
    const showAddTransaction = !!cashierId && !shiftId;
    const [renderDialog, toggleDialog] = useCreateCashierTransactionDialog(cashierId || '');
    const { data, isLoading } = useShiftTransactionData(tableState.pagination, cashierId, shiftId);

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
            <TableHeader
                title={lt('CashierTransactionPlural')}
                subTitle={data?.totalRowCount}
                rightSide={
                    showAddTransaction ? (
                        <Button size="small" icon={IconName.Add} type="button" onClick={toggleDialog}>
                            {lt('CashierShiftAddTransaction')}
                        </Button>
                    ) : null
                }
            />

            <Table
                data-test-id="shift-transaction-table"
                loading={isLoading}
                uniqueRowId="number"
                data={data?.data || []}
                amountOfRows={data?.totalRowCount || 0}
            />
            {renderDialog()}
        </TableProvider>
    );
};
